import { useEffect, useState } from 'react';
import type { UiGuidanceEvent } from '../../types';

interface UiGuidanceLayerProps {
  guidance: UiGuidanceEvent | null;
  onClear: () => void;
}

interface TargetBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

function findTarget(target?: string): HTMLElement | null {
  if (!target || typeof document === 'undefined') return null;
  const escapedTarget = target.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return document.querySelector<HTMLElement>(
    `[data-agent-target="${escapedTarget}"], [data-agent-runtime-target="${escapedTarget}"]`,
  );
}

function getTargetBox(element: HTMLElement): TargetBox {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

export function UiGuidanceLayer({ guidance, onClear }: UiGuidanceLayerProps) {
  const [box, setBox] = useState<TargetBox | null>(null);

  useEffect(() => {
    if (!guidance || guidance.action === 'clear_guidance') {
      setBox(null);
      return;
    }

    const element = findTarget(guidance.target);
    if (!element) {
      setBox(null);
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

    const updateBox = () => setBox(getTargetBox(element));
    const updateTimer = window.setTimeout(updateBox, 250);
    updateBox();

    const handleTargetClick = () => {
      onClear();
    };

    element.addEventListener('click', handleTargetClick, { once: true });
    window.addEventListener('resize', updateBox);
    window.addEventListener('scroll', updateBox, true);

    return () => {
      window.clearTimeout(updateTimer);
      element.removeEventListener('click', handleTargetClick);
      window.removeEventListener('resize', updateBox);
      window.removeEventListener('scroll', updateBox, true);
    };
  }, [guidance, onClear]);

  if (!guidance || guidance.action === 'clear_guidance' || !box) {
    return null;
  }

  const showPulse = guidance.action === 'tap_cue';
  const showLabel = Boolean(guidance.message);

  return (
    <div className="pointer-events-none fixed inset-0 z-[70]" aria-hidden="true">
      <div
        className="absolute rounded-lg border-2 border-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.18),0_12px_28px_rgba(15,23,42,0.18)] transition-all duration-150"
        style={{
          top: box.top - 6,
          left: box.left - 6,
          width: box.width + 12,
          height: box.height + 12,
        }}
      />

      {showPulse && (
        <div
          className="absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-emerald-500 bg-emerald-400/20 animate-ping"
          style={{
            top: box.top + box.height / 2,
            left: box.left + box.width / 2,
          }}
        />
      )}

      {showLabel && (
        <div
          className="absolute max-w-[220px] rounded-md bg-slate-950 px-3 py-2 text-xs font-medium leading-snug text-white shadow-lg"
          style={{
            top: Math.max(12, box.top - 46),
            left: Math.min(Math.max(12, box.left), window.innerWidth - 240),
          }}
        >
          {guidance.message}
        </div>
      )}
    </div>
  );
}
